import { el } from './dom';
import { renderFormFields } from './form-fields';
import { DateStrings } from './date-picker';
import { SurveyAnswer, SurveyQuestion } from './types';

// Shared per-question renderer (4 question types + the embedded-form type), extracted from
// SurveyEngine so both the popup/slide-in survey card (surveys.ts) and an operator-sent
// survey rendered inline in the chat (chat.ts) share one implementation.
// (docs/widget.md W4, docs/forms-in-chat.md §survey)

export interface QuestionStrings extends DateStrings {
  otherPlaceholder: string;
  textPlaceholder: string;
  followPlaceholder: string;
  optional: string;
  other: string;
  npsLow: string;
  npsHigh: string;
  choose: string;
  yes: string;
}

/** rating 1–5 rendered as an emoji face scale (worst → best), Atlassian-style */
const FACES = ['\u{1F620}', '\u{1F641}', '\u{1F610}', '\u{1F642}', '\u{1F604}']; // 😠 🙁 😐 🙂 😄

export function renderQuestion(
  question: SurveyQuestion,
  answers: Map<string, SurveyAnswer>,
  t: QuestionStrings,
): HTMLElement {
  const wrap = el('div', { class: 'cb-q' });
  const title = el('div', { class: 'cb-qtext', text: question.text });
  if (question.required) title.appendChild(el('span', { class: 'cb-req', text: ' *' }));
  wrap.appendChild(title);

  // choice/text: followUpText (if authored) stays a static hint — otherText is
  // reserved for the «other» option there; scale types get the progressive reveal.
  if (question.followUpText && (question.type === 'multiple_choice' || question.type === 'text')) {
    wrap.appendChild(el('div', { class: 'cb-qhint', text: question.followUpText }));
  }

  switch (question.type) {
    case 'multiple_choice':
      renderChoice(wrap, question, answers, t);
      break;
    case 'text': {
      const area = el('textarea', {
        class: 'cb-textarea',
        attrs: { placeholder: t.textPlaceholder, maxlength: '2000', 'aria-label': question.text },
        on: {
          input: () => {
            const value = area.value.trim();
            if (value) answers.set(question.qid, { qid: question.qid, value: area.value });
            else answers.delete(question.qid);
          },
        },
      });
      wrap.appendChild(area);
      break;
    }
    case 'rating':
      renderRating(wrap, question, answers, t);
      break;
    case 'nps':
      renderNps(wrap, question, answers, t);
      break;
    case 'form':
      renderForm(wrap, question, answers, t);
      break;
  }
  return wrap;
}

// ---------- form question (embedded saved Form, 15 field types) ----------

/**
 * Renders a `form`-type question: the referenced form's fields, collected into a
 * single `{ fieldKey: value }` object stored as the question's answer value. The
 * form is considered answered once any field carries a meaningful value.
 */
function renderForm(
  wrap: HTMLElement,
  question: SurveyQuestion,
  answers: Map<string, SurveyAnswer>,
  t: QuestionStrings,
): void {
  const form = question.form;
  if (!form || !form.fields?.length) return; // form deleted/empty — nothing to render

  if (form.description) {
    wrap.appendChild(el('div', { class: 'cb-qhint', text: form.description }));
  }

  const values: Record<string, unknown> = {};
  const sync = () => {
    const filled = Object.values(values).some((v) =>
      Array.isArray(v) ? v.length > 0 : v !== '' && v !== false && v != null,
    );
    if (filled) answers.set(question.qid, { qid: question.qid, value: { ...values } });
    else answers.delete(question.qid);
  };

  wrap.appendChild(renderFormFields(form.fields, values, sync, t));
}

/**
 * Optional follow-up prompt for scale questions (rating/nps), Atlassian-style:
 * hidden until the visitor picks a value, then slides open under the scale.
 * The comment travels as `otherText` on the same answer (accepted by the DTO).
 */
function makeFollowUp(
  question: SurveyQuestion,
  answers: Map<string, SurveyAnswer>,
  t: QuestionStrings,
): { node: HTMLElement | null; reveal: () => void } {
  if (!question.followUpText) return { node: null, reveal: () => undefined };

  const area = el('textarea', {
    class: 'cb-textarea',
    attrs: { placeholder: t.followPlaceholder, maxlength: '2000', 'aria-label': question.followUpText },
    on: {
      input: () => {
        const answer = answers.get(question.qid);
        if (!answer) return; // scale not picked yet — nothing to attach to
        const text = area.value.trim();
        if (text) answer.otherText = area.value;
        else delete answer.otherText;
      },
    },
  });
  const label = el('div', { class: 'cb-followlabel', text: question.followUpText + ' ' });
  label.appendChild(el('span', { class: 'cb-optional', text: t.optional }));
  const node = el('div', { class: 'cb-follow' }, [label, area]);

  let revealed = false;
  const reveal = () => {
    if (revealed) return;
    revealed = true;
    node.classList.add('open');
  };
  return { node, reveal };
}

function renderChoice(
  wrap: HTMLElement,
  question: SurveyQuestion,
  answers: Map<string, SurveyAnswer>,
  t: QuestionStrings,
): void {
  const otherInput = el('input', {
    class: 'cb-textarea cb-otherbox',
    attrs: { type: 'text', placeholder: t.otherPlaceholder, maxlength: '2000' },
    on: {
      input: () => {
        answers.set(question.qid, { qid: question.qid, value: 'other', otherText: otherInput.value });
      },
    },
  });
  otherInput.style.display = 'none';

  const buttons: HTMLButtonElement[] = [];
  const select = (btn: HTMLButtonElement, value: string) => {
    buttons.forEach((b) => {
      b.classList.toggle('sel', b === btn);
      b.setAttribute('aria-checked', b === btn ? 'true' : 'false');
    });
    if (value === 'other') {
      otherInput.style.display = 'block';
      answers.set(question.qid, { qid: question.qid, value: 'other', otherText: otherInput.value });
      otherInput.focus();
    } else {
      otherInput.style.display = 'none';
      answers.set(question.qid, { qid: question.qid, value });
    }
  };

  const group = el('div', { attrs: { role: 'radiogroup', 'aria-label': question.text } });
  const makeOpt = (label: string, value: string) => {
    const btn = el(
      'button',
      {
        class: 'cb-opt',
        attrs: { type: 'button', role: 'radio', 'aria-checked': 'false' },
        on: { click: () => select(btn, value) },
      },
      [el('span', { class: 'cb-radio' }), el('span', { text: label })],
    );
    buttons.push(btn);
    group.appendChild(btn);
  };

  for (const option of question.options ?? []) makeOpt(option, option);
  if (question.allowOther) makeOpt(t.other, 'other');
  wrap.appendChild(group);
  if (question.allowOther) wrap.appendChild(otherInput);
}

/** picks/re-picks a scale value, carrying any follow-up comment already typed */
function setScaleAnswer(question: SurveyQuestion, answers: Map<string, SurveyAnswer>, value: number): void {
  const previous = answers.get(question.qid);
  const answer: SurveyAnswer = { qid: question.qid, value };
  if (previous?.otherText) answer.otherText = previous.otherText;
  answers.set(question.qid, answer);
}

function renderRating(
  wrap: HTMLElement,
  question: SurveyQuestion,
  answers: Map<string, SurveyAnswer>,
  t: QuestionStrings,
): void {
  const followUp = makeFollowUp(question, answers, t);
  const row = el('div', { class: 'cb-faces', attrs: { role: 'radiogroup', 'aria-label': question.text } });
  const faces: HTMLButtonElement[] = [];
  for (let value = 1; value <= 5; value++) {
    const face = el('button', {
      class: 'cb-face',
      text: FACES[value - 1],
      attrs: { type: 'button', role: 'radio', 'aria-checked': 'false', 'aria-label': `${value} / 5` },
      on: {
        click: () => {
          setScaleAnswer(question, answers, value);
          faces.forEach((f) => {
            f.classList.toggle('sel', f === face);
            f.setAttribute('aria-checked', f === face ? 'true' : 'false');
          });
          followUp.reveal();
        },
      },
    });
    faces.push(face);
    row.appendChild(face);
  }
  wrap.appendChild(row);
  if (followUp.node) wrap.appendChild(followUp.node);
}

function renderNps(
  wrap: HTMLElement,
  question: SurveyQuestion,
  answers: Map<string, SurveyAnswer>,
  t: QuestionStrings,
): void {
  const followUp = makeFollowUp(question, answers, t);
  const row = el('div', { class: 'cb-nps', attrs: { role: 'radiogroup', 'aria-label': question.text } });
  const buttons: HTMLButtonElement[] = [];
  for (let value = 0; value <= 10; value++) {
    const btn = el('button', {
      class: 'cb-npsbtn',
      text: String(value),
      attrs: { type: 'button', role: 'radio', 'aria-checked': 'false' },
      on: {
        click: () => {
          setScaleAnswer(question, answers, value);
          buttons.forEach((b) => {
            b.classList.toggle('sel', b === btn);
            b.setAttribute('aria-checked', b === btn ? 'true' : 'false');
          });
          followUp.reveal();
        },
      },
    });
    buttons.push(btn);
    row.appendChild(btn);
  }
  wrap.appendChild(row);
  wrap.appendChild(
    el('div', { class: 'cb-npslabels' }, [
      el('span', { text: t.npsLow }),
      el('span', { text: t.npsHigh }),
    ]),
  );
  if (followUp.node) wrap.appendChild(followUp.node);
}
